import React from "react";
import { Icon, mergeStyles } from "@fluentui/react";
import { DnsSecStatus, IRcaDaneValidationDnsSecResult } from "../../models/IRcaDaneValidationDnsSecResult";
import { mergeStyleSets } from "@fluentui/merge-styles";
import { getIconColors } from "../TestResultNode";
import { IRcaDaneValidationDaneResult } from "../../models/IRcaDaneValidationDaneResult";
import { RcaTestValidationStatus } from "../../models/RcaTestValidationStatus";
import { DeferredTooltipHost } from "../DeferredTooltipHost";

const NA = "—";

/**
 * @param props The component properties
 * @returns The markup
 */
export const DaneValidationResultTableRowDnsIcon: React.FC<{
  /**
   * DNS status
   */
  dnsSecStatus: IRcaDaneValidationDnsSecResult | string;
}> = ({ dnsSecStatus }) => {
  const iconColors = getIconColors();
  const classNames = mergeStyleSets({
    wrapper: {
      paddingBottom: 16,
      marginTop: -8,
    },
    iconHeader: {
      padding: 0,
      fontSize: 16,
    },
    iconSuccess: {
      color: iconColors.Success,
    },
    iconError: {
      color: iconColors.Error,
    },
    iconWarning: {
      color: iconColors.SuccessWithWarningsMid,
    },
    noRecords: {
      fontSize: 14,
      paddingTop: 5,
      paddingBottom: 15,
    },
  });

  if (typeof dnsSecStatus === "string") {
    return (
      <div style={{ textAlign: "center" }} aria-label={dnsSecStatus}>
        {dnsSecStatus}
      </div>
    );
  }

  const statusString = dnsSecStatus.Status;
  const icon =
    statusString === DnsSecStatus.Unknown
      ? "StatusCircleQuestionMark"
      : statusString === DnsSecStatus.Success || statusString === DnsSecStatus.Disabled
      ? "CompletedSolid"
      : "StatusErrorFull";

  const style =
    statusString === DnsSecStatus.Unknown
      ? classNames.iconWarning
      : statusString === DnsSecStatus.Success || statusString === DnsSecStatus.Disabled
      ? classNames.iconSuccess
      : classNames.iconError;

  return (
    <div style={{ textAlign: "center" }} role="img" aria-label={dnsSecStatus.Details}>
      <DeferredTooltipHost content={dnsSecStatus.Details} aria-hidden={true}>
        <Icon iconName={icon} aria-hidden={true} styles={{ root: mergeStyles(style, classNames.iconHeader) }} />
      </DeferredTooltipHost>
    </div>
  );
};

/**
 * @param props The component properties
 * @returns The markup
 */
export const DaneValidationResultTableRowDnsSecIcon: React.FC<{
  /**
   * DNSSEC status
   */
  dnsSecStatus: IRcaDaneValidationDnsSecResult | string;
}> = ({ dnsSecStatus }) => {
  const iconColors = getIconColors();
  const classNames = mergeStyleSets({
    wrapper: {
      paddingBottom: 16,
      marginTop: -8,
    },
    iconHeader: {
      padding: 0,
      fontSize: 16,
    },
    iconSuccess: {
      color: iconColors.Success,
    },
    iconError: {
      color: iconColors.Error,
    },
    iconWarning: {
      color: iconColors.SuccessWithWarningsMid,
    },
    noRecords: {
      fontSize: 14,
      paddingTop: 5,
      paddingBottom: 15,
    },
  });

  if (typeof dnsSecStatus === "string") {
    return (
      <div style={{ textAlign: "center" }} role="img" aria-label={dnsSecStatus}>
        {dnsSecStatus}
      </div>
    );
  }

  const statusString = dnsSecStatus.Status;
  const icon =
    statusString === DnsSecStatus.Disabled
      ? "DaneValidationWarningIcon"
      : statusString === DnsSecStatus.Unknown
      ? "StatusCircleQuestionMark"
      : statusString === DnsSecStatus.Success
      ? "CompletedSolid"
      : "StatusErrorFull";

  const style =
    statusString === DnsSecStatus.Disabled || statusString === DnsSecStatus.Unknown
      ? classNames.iconWarning
      : statusString === DnsSecStatus.Success
      ? classNames.iconSuccess
      : classNames.iconError;

  return (
    <div style={{ textAlign: "center" }} role="img" aria-label={dnsSecStatus.Details}>
      <DeferredTooltipHost content={dnsSecStatus.Details} aria-hidden={true}>
        <Icon iconName={icon} aria-hidden={true} styles={{ root: mergeStyles(style, classNames.iconHeader) }} />
      </DeferredTooltipHost>
    </div>
  );
};

/**
 * @param props The component properties
 * @returns The markup
 */
export const DaneValidationResultTableRowTlsaIcon: React.FC<{
  /**
   * Is TLSA record usable
   */
  isUsable: boolean | string | null;
}> = ({ isUsable }) => {
  const iconColors = getIconColors();
  const classNames = mergeStyleSets({
    wrapper: {
      paddingBottom: 16,
      marginTop: -8,
    },
    iconHeader: {
      padding: 0,
      fontSize: 16,
    },
    iconSuccess: {
      color: iconColors.Success,
    },
    iconError: {
      color: iconColors.Error,
    },
    iconWarning: {
      color: iconColors.SuccessWithWarningsMid,
    },
    noRecords: {
      fontSize: 14,
      paddingTop: 5,
      paddingBottom: 15,
    },
  });

  if (typeof isUsable === "string") {
    return (
      <div style={{ textAlign: "center" }} aria-label={isUsable}>
        {isUsable}
      </div>
    );
  }

  const icon = isUsable === null ? "DaneValidationWarningIcon" : isUsable === true ? "CompletedSolid" : "StatusErrorFull";
  const style = isUsable === null ? classNames.iconWarning : isUsable === true ? classNames.iconSuccess : classNames.iconError;
  const tooltip: string =
    isUsable === null
      ? WebsiteStrings.DaneMissingTlsa
      : isUsable === true
      ? WebsiteStrings.DaneUsableTlsa
      : WebsiteStrings.DaneUnusableTlsa;

  return (
    <div style={{ textAlign: "center" }} role="img" aria-label={tooltip}>
      <DeferredTooltipHost content={tooltip} aria-hidden={true}>
        <Icon iconName={icon} aria-hidden={true} styles={{ root: mergeStyles(style, classNames.iconHeader) }} />
      </DeferredTooltipHost>
    </div>
  );
};

/**
 * @param props The component properties
 * @returns The markup
 */
export const DaneValidationResultTableRowDaneIcon: React.FC<{
  /**
   * DANE status
   */
  dane: IRcaDaneValidationDaneResult | string;
}> = ({ dane }) => {
  const iconColors = getIconColors();
  const classNames = mergeStyleSets({
    wrapper: {
      paddingBottom: 16,
      marginTop: -8,
    },
    iconHeader: {
      padding: 0,
      fontSize: 16,
    },
    iconSuccess: {
      color: iconColors.Success,
    },
    iconError: {
      color: iconColors.Error,
    },
    iconWarning: {
      color: iconColors.SuccessWithWarningsMid,
    },
    noRecords: {
      fontSize: 14,
      paddingTop: 5,
      paddingBottom: 15,
    },
  });

  if (typeof dane === "string") {
    return (
      <div style={{ textAlign: "center" }} aria-label={dane}>
        {dane}
      </div>
    );
  }

  if (dane === null || dane.Status === RcaTestValidationStatus.Skipped) {
    return (
      <div style={{ textAlign: "center" }} aria-label={dane.Details}>
        <DeferredTooltipHost content={dane.Details}>
          <div>{NA}</div>
        </DeferredTooltipHost>
      </div>
    );
  }

  const icon =
    dane.Status === RcaTestValidationStatus.Success
      ? "CompletedSolid"
      : dane.Status === RcaTestValidationStatus.Failed
      ? "StatusErrorFull"
      : "DaneValidationWarningIcon";
  const style =
    dane.Status === RcaTestValidationStatus.Success
      ? classNames.iconSuccess
      : dane.Status === RcaTestValidationStatus.Failed
      ? classNames.iconError
      : classNames.iconWarning;
  const ariaLabel = dane.Status === RcaTestValidationStatus.Failed ? WebsiteStrings.DaneValidationFailed : dane.Details;

  return (
    <div style={{ textAlign: "center" }} role="img" aria-label={ariaLabel}>
      <DeferredTooltipHost content={dane.Details} aria-hidden={true}>
        <Icon iconName={icon} aria-hidden={true} styles={{ root: mergeStyles(style, classNames.iconHeader) }} />
      </DeferredTooltipHost>
    </div>
  );
};
